import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reduxService';
import { EProjectType } from '../enum/project';
import { IDynamicProjectOutputData, IAASHTOResults, IProject, IPavedData, IAirportData } from '../api/IApiResponse';
import { logDebug, logError, logWarn, LogTag } from '../utils/utils';
import { pdfImageCaptureService, PdfImageCaptureService, IAirportImages, IRailwayImages, IContainerYardImages, IUnpavedImages, IPavedImages } from '../services/PdfImageCaptureService';
import { PavedCalculationService } from '../services/calculation/PavedCalculationService';
import { getContainerYardResultInvalidity } from '../ContainerYard/utils/containerYardResultValidity';

type CapturedImageTypes = IAirportImages | IRailwayImages | IContainerYardImages | IUnpavedImages | IPavedImages | null;

// PDF capture timing constants
const PDF_IMAGE_CAPTURE_DELAY = 1500; // ms - Delay before starting image capture after results load
const PDF_MINIMUM_CAPTURE_DELAY = 1000; // ms - Minimum delay to allow canvas rendering

interface UsePdfExportResult {
    isGeneratingPdfImages: boolean;
    pdfImageError: Error | null;
    currentProjectImages: CapturedImageTypes;
    captureProjectImages: (projectType: EProjectType) => Promise<void>;
    handleExportToPDF: () => Promise<void>;
    clearPdfImageError: () => void;
    getProjectImagesFromStorage: () => CapturedImageTypes;
}

/**
 * Custom hook for PDF export functionality
 *
 * This hook handles:
 * - Image capture for different project types
 * - PDF generation orchestration
 * - SessionStorage management for captured images
 * - Error handling for PDF operations
 * - Container Yard result validation
 */
export const usePdfExport = (dynamicProjectData: IProject | null, projectType: EProjectType): UsePdfExportResult => {
    const [isGeneratingPdfImages, setIsGeneratingPdfImages] = useState(false);
    const [pdfImageError, setPdfImageError] = useState<Error | null>(null);
    const [currentProjectImages, setCurrentProjectImages] = useState<CapturedImageTypes>(null);

    // Ref to track active timeout for cleanup
    const pdfRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Bumped by every capture start and every discard: an awaited capture applies its outcome only while it is still
    // the latest, so a capture superseded mid-flight cannot store stale images or raise a stale error
    const captureGenerationRef = useRef(0);

    // Redux selectors
    const resultsData = useSelector((state: RootState) => state.Dynamic.results.data?.outputData);
    const aashtoResults = useSelector((state: RootState) => state.Dynamic.project.data?.aashtoResults);

    // 🔧 FIX: Extract custom design enable flags for Paved projects to trigger auto-capture when they change
    const enableSurfaceDesign = (dynamicProjectData?.projectInfo?.data as IPavedData)?.surfaceDesign?.enableSurfaceDesign;
    const enableBaseDesign = (dynamicProjectData?.projectInfo?.data as IPavedData)?.baseDesign?.enableBaseDesign;
    const enableSubbaseDesign = (dynamicProjectData?.projectInfo?.data as IPavedData)?.subbaseDesign?.enableSubbaseDesign;

    // Helper function to get project images from sessionStorage
    const getProjectImagesFromStorage = useCallback(() => {
        if (!dynamicProjectData?.guid) {
            return null;
        }

        // ✅ Use centralized key generator (BUG-001 fix)
        const storageKey = PdfImageCaptureService.getStorageKey(projectType, dynamicProjectData.guid);
        const stored = sessionStorage.getItem(storageKey);

        if (stored) {
            try {
                return JSON.parse(stored);
            } catch {
                sessionStorage.removeItem(storageKey);
                return null;
            }
        }

        return null;
    }, [projectType, dynamicProjectData?.guid]);

    // Update currentProjectImages when project or guid changes
    useEffect(() => {
        setCurrentProjectImages(getProjectImagesFromStorage());
    }, [getProjectImagesFromStorage]);

    // Helper function to check if Container Yard results are valid for PDF capture
    const isContainerYardResultValid = useCallback(
        (projectType: EProjectType, resultsData: IDynamicProjectOutputData | undefined): boolean => {
            if (projectType !== EProjectType.container_yard && projectType !== EProjectType.parking_deck) {
                return true; // Only validate container_yard and parking_deck projects
            }

            if (!resultsData) {
                logWarn('usePdfExport', 'PDF Capture: No results data available for validation', undefined, LogTag.PDF_IMAGES);
                return false;
            }

            const deckDesign = resultsData?.deckDesign;
            if (!deckDesign || typeof deckDesign.unreinforcedHeight !== 'number' || typeof deckDesign.reinforcedHeight !== 'number') {
                logWarn('usePdfExport', 'PDF Capture: Deck design data not available for validation', undefined, LogTag.PDF_IMAGES);
                return false;
            }

            // Same check as the Results page: no capture where Results shows the alert instead of the drawings
            const invalidity = getContainerYardResultInvalidity({
                projectType,
                method: dynamicProjectData?.method,
                unreinforcedHeight: deckDesign.unreinforcedHeight,
                reinforcedHeight: deckDesign.reinforcedHeight,
            });

            if (invalidity) {
                logWarn('usePdfExport', `PDF Capture: Skipping capture due to invalid result (${invalidity})`, deckDesign, LogTag.PDF_IMAGES);
            }

            return invalidity === null;
        },
        [dynamicProjectData?.method]
    );

    // Without valid results there are no drawings to capture: images stored by an earlier valid run must not reach
    // the PDF, and an earlier capture error must not keep the PDF error banner up
    const discardStoredProjectImages = useCallback(() => {
        captureGenerationRef.current += 1;
        // The superseded capture skips its finally, so its generating flag is switched off here
        setIsGeneratingPdfImages(false);
        if (dynamicProjectData?.guid) {
            sessionStorage.removeItem(PdfImageCaptureService.getStorageKey(projectType, dynamicProjectData.guid));
        }
        setCurrentProjectImages(null);
        setPdfImageError(null);
    }, [projectType, dynamicProjectData?.guid]);

    // Main function to capture project images for PDF generation
    const captureProjectImages = useCallback(
        async (projectType: EProjectType) => {
            // 🔧 FIX: Only check resultsData for Container Yard/Parking Deck
            // For Paved/Airport with custom design, canvas can be rendered without resultsData
            const requiresResultsData = projectType === EProjectType.container_yard || projectType === EProjectType.parking_deck || projectType === EProjectType.unpaved;

            if (requiresResultsData && !resultsData) {
                logWarn('usePdfExport', 'PDF Export: resultsData not available, skipping image capture.', undefined, LogTag.PDF_IMAGES);
                return;
            }

            // Check if Container Yard/Parking Deck results are valid before attempting capture
            if (resultsData && !isContainerYardResultValid(projectType, resultsData)) {
                logWarn('usePdfExport', 'PDF Capture: Skipping image capture due to invalid results', undefined, LogTag.PDF_IMAGES);
                discardStoredProjectImages();
                return;
            }

            captureGenerationRef.current += 1;
            const captureGeneration = captureGenerationRef.current;
            const isSuperseded = () => captureGeneration !== captureGenerationRef.current;

            try {
                setIsGeneratingPdfImages(true);
                setPdfImageError(null);

                logDebug('usePdfExport', `Starting simplified ${projectType} capture...`, undefined, LogTag.PDF_IMAGES);

                let capturedImages: CapturedImageTypes = null;

                switch (projectType) {
                    case EProjectType.container_yard:
                    case EProjectType.parking_deck:
                        capturedImages = await pdfImageCaptureService.captureContainerYardImages({ jpegQuality: 0.7 });
                        break;
                    case EProjectType.unpaved:
                        capturedImages = await pdfImageCaptureService.captureUnpavedImages({ jpegQuality: 0.7 });
                        break;
                    case EProjectType.paved:
                        // Build trafficData for paved projects from aashtoResults Redux selector
                        const pavedResultsData = aashtoResults as IAASHTOResults;
                        const pavedInputData = dynamicProjectData?.projectInfo?.data as IPavedData | undefined;
                        const pavedInputEsal = pavedInputData?.trafficLoad?.esalFinalValue;
                        let trafficData = undefined;

                        if (pavedResultsData?.detailedCalculations?.esalFactors) {
                            const esalFactors = pavedResultsData.detailedCalculations.esalFactors;
                            // Calculate thickness saving percentage
                            let thicknessSaving = 0;

                            if (pavedResultsData?.detailedCalculations?.softwareDesign) {
                                const sd = pavedResultsData.detailedCalculations.softwareDesign;
                                const unstabilizedTotal = (sd.surface?.unstabilised || 0) + (sd.base?.unstabilised || 0) + (sd.subbase?.unstabilised || 0);
                                const stabilizedTotal = (sd.surface?.stabilised || 0) + (sd.base?.stabilised || 0) + (sd.subbase?.stabilised || 0);

                                if (unstabilizedTotal > 0) {
                                    thicknessSaving = ((unstabilizedTotal - stabilizedTotal) / unstabilizedTotal) * 100;
                                }
                            }

                            trafficData = {
                                unstabilized: {
                                    passesFirstYear: esalFactors.esalUnstabilised || 0,
                                    w18: esalFactors.logW18Unstabilised || 0,
                                    totalEsal: pavedInputEsal ?? esalFactors.esalUnstabilised ?? 0,
                                },
                                increasedPassages: {
                                    passesFirstYear: esalFactors.esalStabilised || 0,
                                    newW18: esalFactors.logW18Stabilised || 0,
                                    tbr: esalFactors.tbr || 0,
                                },
                                optimized: {
                                    passesFirstYear: esalFactors.esalUnstabilised || 0,
                                    w18: esalFactors.logW18Unstabilised || 0,
                                    thicknessSaving,
                                },
                            };
                        }

                        // Capture images with trafficData and projectData included
                        const projectDataForCapture = dynamicProjectData?.projectInfo?.data;

                        // Calculate table data using PavedCalculationService for data-driven approach
                        let designTableData = undefined;
                        if (pavedResultsData?.detailedCalculations && dynamicProjectData?.projectInfo?.data) {
                            try {
                                const calculationService = new PavedCalculationService(pavedResultsData, projectDataForCapture);

                                // Get design inputs from project data
                                const pavedProjectData = dynamicProjectData.projectInfo.data as IPavedData;
                                const surfaceDesignInputs = {
                                    baseThickness: pavedProjectData?.surfaceDesign?.baseThickness || 0.2,
                                    subbaseThickness: pavedProjectData?.surfaceDesign?.subbaseThickness || 0.4,
                                    surfaceThickness: 0, // Will be calculated
                                };

                                const baseDesignInputs = {
                                    surfaceThickness: pavedProjectData?.baseDesign?.surfaceThickness || 0.1,
                                    subbaseThickness: pavedProjectData?.baseDesign?.subbaseThickness || 0.4,
                                    baseThickness: 0, // Will be calculated
                                };

                                const subbaseDesignInputs = {
                                    surfaceThickness: pavedProjectData?.subbaseDesign?.surfaceThickness || 0.1,
                                    baseThickness: pavedProjectData?.subbaseDesign?.baseThickness || 0.2,
                                    subbaseThickness: 0, // Will be calculated
                                };

                                // Calculate table data for all design scenarios
                                const allTableData = calculationService.calculateAllDesignTableData(surfaceDesignInputs, baseDesignInputs, subbaseDesignInputs);
                                designTableData = allTableData;
                            } catch (calculationError) {
                                logError('usePdfExport', 'Failed to calculate table data, falling back to DOM scraping', calculationError, LogTag.DYNAMIC_CANVAS);
                                designTableData = undefined;
                            }
                        }

                        capturedImages = await pdfImageCaptureService.capturePavedImages({
                            jpegQuality: 0.8,
                            trafficData,
                            projectData: projectDataForCapture,
                            designTableData,
                        });
                        break;

                    case EProjectType.airport:
                        // Add project data and design table data support for Airport (same as Paved)
                        const airportProjectDataForCapture = dynamicProjectData?.projectInfo?.data;
                        const airportResultsData = aashtoResults as IAASHTOResults;
                        let airportDesignTableData = undefined;

                        if (airportResultsData?.detailedCalculations && airportProjectDataForCapture) {
                            try {
                                const calculationService = new PavedCalculationService(airportResultsData, airportProjectDataForCapture);

                                // Get design inputs from project data (same structure as Paved)
                                const airportProjectData = dynamicProjectData.projectInfo.data as IAirportData;
                                const surfaceDesignInputs = {
                                    baseThickness: airportProjectData?.surfaceDesign?.baseThickness || 0.2,
                                    subbaseThickness: airportProjectData?.surfaceDesign?.subbaseThickness || 0.4,
                                    surfaceThickness: 0,
                                };

                                const baseDesignInputs = {
                                    surfaceThickness: airportProjectData?.baseDesign?.surfaceThickness || 0.1,
                                    subbaseThickness: airportProjectData?.baseDesign?.subbaseThickness || 0.4,
                                    baseThickness: 0,
                                };

                                const subbaseDesignInputs = {
                                    surfaceThickness: airportProjectData?.subbaseDesign?.surfaceThickness || 0.1,
                                    baseThickness: airportProjectData?.subbaseDesign?.baseThickness || 0.2,
                                    subbaseThickness: 0,
                                };

                                const allTableData = calculationService.calculateAllDesignTableData(surfaceDesignInputs, baseDesignInputs, subbaseDesignInputs);
                                airportDesignTableData = allTableData;
                            } catch (error) {
                                logError('usePdfExport', 'Error calculating Airport table data:', error, LogTag.DYNAMIC_CANVAS);
                                airportDesignTableData = undefined;
                            }
                        }

                        capturedImages = await pdfImageCaptureService.captureAirportImages({
                            jpegQuality: 0.8,
                            projectData: airportProjectDataForCapture,
                            designTableData: airportDesignTableData,
                        });
                        break;

                    case EProjectType.railways:
                        capturedImages = await pdfImageCaptureService.captureRailwayImages({ jpegQuality: 0.8 });
                        break;
                    case EProjectType.working_platforms:
                        // Working platforms uses same capture logic as container yard
                        capturedImages = await pdfImageCaptureService.captureContainerYardImages({ jpegQuality: 0.7 });
                        break;
                }

                if (isSuperseded()) {
                    logDebug('usePdfExport', `Discarding superseded ${projectType} capture`, undefined, LogTag.PDF_IMAGES);
                    return;
                }

                // Store captured images in sessionStorage
                if (capturedImages && dynamicProjectData?.guid) {
                    // ✅ Use centralized key generator (BUG-001 fix)
                    const storageKey = PdfImageCaptureService.getStorageKey(projectType, dynamicProjectData.guid);
                    sessionStorage.setItem(storageKey, JSON.stringify(capturedImages));
                    logDebug(
                        'usePdfExport',
                        `Stored ${projectType} images in sessionStorage`,
                        {
                            storageKey,
                            imageCount: Object.keys(capturedImages).length,
                            hasTrafficData: projectType === EProjectType.paved ? !!(capturedImages as IPavedImages)?.trafficData : false,
                            trafficDataKeys: projectType === EProjectType.paved && (capturedImages as IPavedImages)?.trafficData ? Object.keys((capturedImages as IPavedImages).trafficData || {}) : [],
                        },
                        LogTag.PDF_IMAGES
                    );
                }

                logDebug('usePdfExport', `Simplified ${projectType} capture completed`, undefined, LogTag.PDF_IMAGES);

                // Refresh the images state after successful capture
                setCurrentProjectImages(getProjectImagesFromStorage());
            } catch (error) {
                if (isSuperseded()) {
                    logDebug('usePdfExport', `Ignoring the failure of a superseded ${projectType} capture`, error, LogTag.PDF_IMAGES);
                    return;
                }
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                const errorObj = error instanceof Error ? error : new Error(errorMessage);
                setPdfImageError(errorObj);
                logError('usePdfExport', `Simplified ${projectType} capture failed:`, error, LogTag.PDF_IMAGES);
            } finally {
                if (!isSuperseded()) {
                    setIsGeneratingPdfImages(false);
                }
            }
        },
        [resultsData, dynamicProjectData, getProjectImagesFromStorage, aashtoResults, isContainerYardResultValid, discardStoredProjectImages]
    );

    // Handle PDF export - delegated to ButtonPanel V2 unified system
    const handleExportToPDF = useCallback(async () => {
        logDebug('usePdfExport', 'PDF Export: Starting PDF generation process...', undefined, LogTag.PDF_EXPORT);

        if (!resultsData || !dynamicProjectData) {
            logWarn('usePdfExport', 'PDF Export: Missing required data for PDF generation', undefined, LogTag.PDF_EXPORT);
            return;
        }

        // All PDF exports are handled by ButtonPanel V2 unified system
        logDebug('usePdfExport', `PDF Export: ${projectType} - delegating to ButtonPanel V2 system`, undefined, LogTag.PDF_EXPORT);
        return;
    }, [resultsData, dynamicProjectData, projectType]);

    // Cleanup active timeouts on unmount
    useEffect(() => {
        return () => {
            if (pdfRetryTimeoutRef.current) {
                clearTimeout(pdfRetryTimeoutRef.current);
                pdfRetryTimeoutRef.current = null;
            }
        };
    }, []);

    const clearPdfImageError = useCallback(() => {
        setPdfImageError(null);
    }, []);

    // Auto-capture logic - trigger image capture when results are available
    useEffect(() => {
        console.log('🔍 [usePdfExport] Auto-capture useEffect TRIGGERED', {
            hasResultsData: !!resultsData,
            hasGuid: !!dynamicProjectData?.guid,
            projectType,
            enableSurfaceDesign,
            enableBaseDesign,
            enableSubbaseDesign,
        });

        let isMounted = true;

        // 🔧 FIX: Only check dynamicProjectData, not resultsData
        // When custom design is enabled, resultsData is empty but canvas are already rendered based on dynamicProjectData
        if (dynamicProjectData?.guid) {
            logDebug('usePdfExport', `Auto-capture triggered for ${projectType}`, { enableSurfaceDesign, enableBaseDesign, enableSubbaseDesign }, LogTag.PDF_IMAGES);

            // Check if Container Yard results are valid before attempting capture (only when resultsData exists)
            if (resultsData && !isContainerYardResultValid(projectType, resultsData)) {
                logWarn('usePdfExport', 'Auto-capture: Skipping due to invalid results', undefined, LogTag.PDF_IMAGES);
                discardStoredProjectImages();
                return;
            }

            // Check if images already exist for immediate availability
            // ✅ Use centralized key generator (BUG-001 fix)
            const storageKey = PdfImageCaptureService.getStorageKey(projectType, dynamicProjectData.guid);
            const existingImages = sessionStorage.getItem(storageKey);
            const captureDelay = existingImages ? PDF_IMAGE_CAPTURE_DELAY : PDF_MINIMUM_CAPTURE_DELAY;

            logDebug(
                'usePdfExport',
                'Auto-capture strategy:',
                {
                    hasExistingImages: !!existingImages,
                    captureDelay: `${captureDelay}ms`,
                    strategy: existingImages ? 'delayed_refresh' : 'immediate_capture',
                },
                LogTag.PDF_IMAGES
            );

            const timer = setTimeout(() => {
                if (isMounted) {
                    captureProjectImages(projectType);
                }
            }, captureDelay);

            return () => {
                isMounted = false;
                clearTimeout(timer);
            };
        } else {
            // Clear images when no results available or no project data
            if (isMounted && dynamicProjectData?.guid) {
                // ✅ Use centralized key generator (BUG-001 fix)
                const storageKey = PdfImageCaptureService.getStorageKey(projectType, dynamicProjectData.guid);
                sessionStorage.removeItem(storageKey);
                logDebug('usePdfExport', 'Auto-capture: Cleared images due to no results', { storageKey }, LogTag.PDF_IMAGES);
            }

            // Clear errors when no project data
            if (!dynamicProjectData) {
                clearPdfImageError();
            }
        }

        return () => {
            isMounted = false;
        };
    }, [
        resultsData,
        projectType,
        dynamicProjectData,
        captureProjectImages,
        isContainerYardResultValid,
        discardStoredProjectImages,
        clearPdfImageError,
        enableSurfaceDesign,
        enableBaseDesign,
        enableSubbaseDesign,
    ]);

    return {
        isGeneratingPdfImages,
        pdfImageError,
        currentProjectImages,
        captureProjectImages,
        handleExportToPDF,
        clearPdfImageError,
        getProjectImagesFromStorage,
    };
};
